const MICROPHONE_FLAGS = {
  signal_too_quiet: "Signal trop faible",
  clipping_risk: "Saturation possible",
  insufficient_activity: "Voix insuffisamment détectée",
};

const TECHNICAL_FLAGS = {
  missing_timestamp: "Horodatage indisponible",
  segment_too_short: "Segment trop court",
  measurement_failed: "Mesure indisponible",
};

const TEXT_LABELS = {
  correct: "Reconnu",
  replace: "Reconnaissance différente",
  missing: "Non reconnu ou non récité",
};

const ACOUSTIC_LABELS = {
  measurement_available_calibration_required: "Mesuré — calibration requise",
  eligible_signal_measurement_unavailable: "Mesure exploitable indisponible",
  rule_target_mapping_unavailable: "Cible acoustique à préciser",
  word_transcription_alignment_unreliable: "Alignement à vérifier",
  dedicated_measurement_missing: "Mesure spécialisée manquante",
};

const QUALITY_LABELS = {
  signal_too_quiet: "Signal trop faible / microphone",
  clipping_risk: "Saturation possible du microphone",
  insufficient_activity: "Activité vocale insuffisante",
  missing_timestamp: "Horodatage ASR indisponible",
  segment_too_short: "Segment audio trop court",
  measurement_failed: "Mesure audio indisponible",
};

function title(value) {
  return String(value || "").replaceAll("_", " ").replaceAll(".", " ").replace(/\b\p{L}/gu, (letter) => letter.toUpperCase());
}

export function buildRecordingQualitySummary(result = {}) {
  const words = result.acoustic_analysis?.expected_word_evidence?.words || [];
  let analyzed = 0; let passed = 0; let microphoneIssueWords = 0; let technicalIssueWords = 0;
  const counts = {};
  for (const word of words) {
    const evidence = word.acoustic_evidence || {};
    const flags = new Set(evidence.quality_flags || []);
    if (evidence.quality_gate == null && flags.size === 0) continue;
    analyzed += 1;
    if (evidence.quality_gate === "passed" && flags.size === 0) passed += 1;
    const microphoneHits = [...flags].filter((flag) => MICROPHONE_FLAGS[flag]);
    const technicalHits = [...flags].filter((flag) => TECHNICAL_FLAGS[flag]);
    if (microphoneHits.length) microphoneIssueWords += 1;
    if (technicalHits.length) technicalIssueWords += 1;
    for (const flag of [...new Set([...microphoneHits, ...technicalHits])]) counts[flag] = (counts[flag] || 0) + 1;
  }
  const ratio = analyzed ? microphoneIssueWords / analyzed : 0;
  let status; let statusLabel; let message; let retakeRecommended = false;
  if (!analyzed) {
    status = "signal_quality_unavailable"; statusLabel = "Qualité du microphone indisponible";
    message = "Aucun segment lié ne permet d’évaluer le microphone. Cela ne constitue pas une faute Tajweed.";
  } else if (microphoneIssueWords >= 2 && ratio >= 0.3) {
    status = "microphone_review_required"; statusLabel = "Microphone à vérifier avant l’interprétation";
    message = "Plusieurs segments présentent un signal faible, saturé ou peu actif. Réenregistrez la récitation avant d’interpréter les écarts ASR.";
    retakeRecommended = true;
  } else if (microphoneIssueWords) {
    status = "localized_signal_issue"; statusLabel = "Signal à vérifier sur quelques mots";
    message = "Le signal est exploitable en partie, avec un problème localisé. Les mots non reconnus ne sont pas automatiquement des erreurs.";
  } else {
    status = "signal_suitable"; statusLabel = "Signal exploitable pour l’analyse descriptive";
    message = "Aucun problème global de microphone n’a été détecté sur les segments liés. Cela ne valide pas la récitation Tajweed.";
  }
  const labels = { ...MICROPHONE_FLAGS, ...TECHNICAL_FLAGS };
  return {
    status, statusLabel, message, analyzedWordCount: analyzed, passedWordCount: passed,
    microphoneIssueWordCount: microphoneIssueWords, technicalIssueWordCount: technicalIssueWords,
    issueCounts: Object.fromEntries(Object.keys(counts).sort().map((key) => [labels[key], counts[key]])),
    retakeRecommended, missingOrUnrecitedWordsAreMicrophoneFaults: false,
    acousticTajweedValidated: false, publicCorrectionCount: 0,
  };
}

export function buildResultPresentationPolicy(quality = {}) {
  if (quality.retakeRecommended) return {
    status: "provisional_signal_review",
    title: "Résultats provisoires — nouvel enregistrement conseillé",
    asrDifferencesActionable: false, pedagogicalFeedbackVisible: false, wordRetryAllowed: false,
    fullRecitationRetakeRecommended: true, technicalDetailsAvailable: true,
    publicTajweedVerdictAllowed: false,
    message: "Le signal global peut fausser la transcription. Réenregistrez la récitation complète avant de travailler un mot isolé.",
  };
  return {
    status: "descriptive_results_available", title: "Résultats descriptifs de la récitation",
    asrDifferencesActionable: true, pedagogicalFeedbackVisible: true, wordRetryAllowed: true,
    fullRecitationRetakeRecommended: false, technicalDetailsAvailable: true,
    publicTajweedVerdictAllowed: false,
    message: "Les différences ASR peuvent guider une nouvelle écoute, sans constituer un verdict Tajweed.",
  };
}

export function buildAcousticBetaSummary(result = {}) {
  const acoustic = result.acoustic_analysis || {};
  const targets = acoustic.phoneme_measurements?.targets || [];
  const evidence = acoustic.runtime_rule_evidence || {};
  const items = evidence.items || [];
  const expected = acoustic.expected_word_evidence || {};
  const measuredTargetCount = targets.filter((target) => target.measurement_status === "measured").length;
  const reviewTargetCount = targets.filter((target) => target.measurement_status === "review_required").length;
  const ruleIds = new Set(items.map((item) => item.curriculum_rule_id || item.rule_id).filter(Boolean));
  const alignmentStatus = expected.alignment_status || evidence.alignment_status || evidence.transcription_alignment_status || acoustic.alignment_status || "indisponible";
  const alignmentLabels = {
    reliable_exact_text_match: "Correspondance textuelle exacte et fiable",
    contextually_verified_words_only: "Mots vérifiés selon le contexte",
    transcription_alignment_unreliable: "Alignement textuel à vérifier",
    indisponible: "Alignement indisponible",
  };
  return {
    alignmentStatus, alignmentStatusLabel: alignmentLabels[alignmentStatus] || "Alignement technique à vérifier",
    reliableWordCount: Number(expected.reliable_word_count || expected.contextually_verified_word_count || evidence.contextually_verified_word_count || evidence.contextually_reliable_word_count || 0),
    expectedWordCount: Number(expected.expected_word_count || evidence.expected_word_count || evidence.transcription_expected_word_count || 0),
    correctWordCount: Number(expected.correct_word_count || evidence.transcription_correct_word_count || 0),
    measuredTargetCount, reviewTargetCount, ruleContextCount: items.length, observedRuleCount: ruleIds.size,
    calibrationCandidateCount: Number(evidence.calibration_candidate_context_count || 0),
    publicCorrectionCount: 0, acousticTajweedValidated: false,
    statusLabel: "Mesures descriptives — calibration en cours",
  };
}

export function buildWordAnalysisTimeline(result = {}) {
  const acoustic = result.acoustic_analysis || {};
  const expected = acoustic.expected_word_evidence || {};
  const evidence = acoustic.runtime_rule_evidence || {};
  const phonemes = acoustic.phoneme_measurements || {};
  const rulesByWord = new Map(); const familiesByWord = new Map(); const statusesByWord = new Map(); const measuredByWord = new Map();
  for (const item of evidence.items || []) {
    const index = item.expected_index;
    if (!Number.isInteger(index)) continue;
    if (item.curriculum_rule_id || item.rule_id) {
      if (!rulesByWord.has(index)) rulesByWord.set(index, new Set());
      rulesByWord.get(index).add(String(item.curriculum_rule_id || item.rule_id));
    }
    if (item.family) {
      if (!familiesByWord.has(index)) familiesByWord.set(index, new Set());
      familiesByWord.get(index).add(title(item.family));
    }
    if (item.evidence_status) {
      if (!statusesByWord.has(index)) statusesByWord.set(index, new Set());
      statusesByWord.get(index).add(String(item.evidence_status));
    }
  }
  for (const target of phonemes.targets || []) {
    if (Number.isInteger(target.expected_index) && target.measurement_status === "measured") measuredByWord.set(target.expected_index, (measuredByWord.get(target.expected_index) || 0) + 1);
  }
  return (expected.words || []).map((word, position) => {
    const index = Number.isInteger(word.expected_index) ? word.expected_index : position;
    const acousticWord = word.acoustic_evidence || {};
    const flags = acousticWord.quality_flags || [];
    const signalQuality = flags.length
      ? flags.map((flag) => QUALITY_LABELS[flag] || title(flag)).join(", ")
      : acousticWord.quality_gate === "passed" ? "Signal mesurable"
        : word.evidence_status === "unavailable" ? "Audio non associé à ce mot" : "À vérifier";
    return {
      word: word.expected_word_surface || word.word || "",
      textStatus: TEXT_LABELS[word.text_status] || "Indisponible",
      startSeconds: Number.isFinite(acousticWord.start_seconds) ? Number(acousticWord.start_seconds.toFixed(3)) : null,
      endSeconds: Number.isFinite(acousticWord.end_seconds) ? Number(acousticWord.end_seconds.toFixed(3)) : null,
      signalQuality, measurementCount: measuredByWord.get(index) || 0,
      families: [...(familiesByWord.get(index) || [])].sort().join(", "),
      rules: [...(rulesByWord.get(index) || [])].map((rule) => title(rule.split(".").at(-1))).sort().join(", "),
      acousticStatus: [...(statusesByWord.get(index) || [])].map((status) => ACOUSTIC_LABELS[status] || title(status)).sort().join(", ") || "Aucun contexte mesuré",
      tajweedVerdict: "désactivé",
    };
  });
}
